/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace poisson
*/
var poisson = {};

/**
* @name cdf
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/cdf}
*/
import cdf from './../../../../base/dists/poisson/cdf';
setReadOnly( poisson, 'cdf', cdf );

/**
* @name Poisson
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/ctor}
*/
import Poisson from './../../../../base/dists/poisson/ctor';
setReadOnly( poisson, 'Poisson', Poisson );

/**
* @name entropy
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/entropy}
*/
import entropy from './../../../../base/dists/poisson/entropy';
setReadOnly( poisson, 'entropy', entropy );

/**
* @name kurtosis
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/kurtosis}
*/
import kurtosis from './../../../../base/dists/poisson/kurtosis';
setReadOnly( poisson, 'kurtosis', kurtosis );

/**
* @name logpmf
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/logpmf}
*/
import logpmf from './../../../../base/dists/poisson/logpmf';
setReadOnly( poisson, 'logpmf', logpmf );

/**
* @name mean
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/mean}
*/
import mean from './../../../../base/dists/poisson/mean';
setReadOnly( poisson, 'mean', mean );

/**
* @name median
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/median}
*/
import median from './../../../../base/dists/poisson/median';
setReadOnly( poisson, 'median', median );

/**
* @name mgf
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/mgf}
*/
import mgf from './../../../../base/dists/poisson/mgf';
setReadOnly( poisson, 'mgf', mgf );

/**
* @name mode
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/mode}
*/
import mode from './../../../../base/dists/poisson/mode';
setReadOnly( poisson, 'mode', mode );

/**
* @name pmf
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/pmf}
*/
import pmf from './../../../../base/dists/poisson/pmf';
setReadOnly( poisson, 'pmf', pmf );

/**
* @name quantile
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/quantile}
*/
import quantile from './../../../../base/dists/poisson/quantile';
setReadOnly( poisson, 'quantile', quantile );

/**
* @name skewness
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/skewness}
*/
import skewness from './../../../../base/dists/poisson/skewness';
setReadOnly( poisson, 'skewness', skewness );

/**
* @name stdev
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/stdev}
*/
import stdev from './../../../../base/dists/poisson/stdev';
setReadOnly( poisson, 'stdev', stdev );

/**
* @name variance
* @memberof poisson
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/poisson/variance}
*/
import variance from './../../../../base/dists/poisson/variance';
setReadOnly( poisson, 'variance', variance );


// EXPORTS //

export default poisson;
